import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Redirect to login on session expiry
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  verifySession: () => api.get('/auth/verify')
};

// Agencies API
export const agenciesAPI = {
  getAll: (params) => api.get('/agencies', { params }),
  getById: (id) => api.get(`/agencies/${id}`),
  create: (data) => api.post('/agencies', data),
  update: (id, data) => api.put(`/agencies/${id}`, data),
  updateStatus: (id, data) => api.patch(`/agencies/${id}/status`, data),
  delete: (id) => api.delete(`/agencies/${id}`),
  getStats: () => api.get('/agencies/stats'),
  getActivationStatus: (id) => api.get(`/agencies/${id}/activation-status`),
  regenerateCode: (id) => api.post(`/agencies/${id}/regenerate-code`)
};

// Visa Management API
export const visaAPI = {
  // Master data
  getCountries: (params) => api.get('/visa/countries', { params }),
  getCategories: () => api.get('/visa/categories'),
  getDocumentTypes: () => api.get('/visa/document-types'),
  createDocumentType: (name) => api.post('/visa/document-types', { name }),
  updateDocumentType: (id, name) => api.put(`/visa/document-types/${id}`, { name }),
  deleteDocumentType: (id) => api.delete(`/visa/document-types/${id}`),
  getProcessingTypes: () => api.get('/visa/processing-types'),

  // Visa Configs
  getConfigs: (params) => api.get('/visa/configs', { params }),
  getConfigById: (id) => api.get(`/visa/configs/${id}`),
  createConfig: (data) => api.post('/visa/configs', data),
  updateConfig: (id, data) => api.put(`/visa/configs/${id}`, data),
  deleteConfig: (id) => api.delete(`/visa/configs/${id}`),
  toggleFeatured: (id) => api.patch(`/visa/configs/${id}/featured`),

  // Visa Pricing
  getPricing: (params) => api.get('/visa/pricing', { params }),
  createPricing: (data) => api.post('/visa/pricing', data),
  updatePricing: (id, data) => api.put(`/visa/pricing/${id}`, data),
  deletePricing: (id) => api.delete(`/visa/pricing/${id}`),
  getPricingHistory: (id) => api.get(`/visa/pricing/${id}/history`),
  bulkUpdatePricing: (updates) => api.put('/visa/pricing/bulk', { updates }),

  // Visa Documents
  getDocuments: (params) => api.get('/visa/documents', { params }),
  createDocument: (data) => api.post('/visa/documents', data),
  updateDocument: (id, data) => api.put(`/visa/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/visa/documents/${id}`),
  uploadSampleDocument: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/visa/documents/${id}/upload-sample`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // AI Config Builder
  aiConfig: (message, conversationHistory = []) => api.post('/visa/ai-config', { message, conversationHistory }),
  aiConfigExecutePending: (pending_actions, nationality_country_id) => api.post('/visa/ai-config/execute-pending', { pending_actions, nationality_country_id })
};

// Visa Applications API
export const visaApplicationsAPI = {
  getAll: (params) => api.get('/visa-applications', { params }),
  getById: (id) => api.get(`/visa-applications/${id}`),
  updateStatus: (id, data) => api.patch(`/visa-applications/${id}/status`, data),
  assignToAgency: (id, data) => api.patch(`/visa-applications/${id}/agency`, data),
  getStats: () => api.get('/visa-applications/stats'),
  getCountries: () => api.get('/visa-applications/countries'),
  getAgencies: () => api.get('/visa-applications/agencies'),
  getDocumentViewUrl: (documentId) => api.get(`/visa-applications/documents/${documentId}/view`),
  addComment: (id, comment) => api.post(`/visa-applications/${id}/comments`, { comment }),
  deleteComment: (id, commentId) => api.delete(`/visa-applications/${id}/comments/${commentId}`),
  sendEmail: (id, data) => api.post(`/visa-applications/${id}/email`, data),
  uploadGrantedVisa: (id, file) => {
    const fd = new FormData();
    fd.append('visa', file);
    // Clear the JSON default so the browser sets multipart/form-data + boundary.
    return api.post(`/visa-applications/${id}/upload-visa`, fd, { headers: { 'Content-Type': undefined } });
  },
  delete: (id) => api.delete(`/visa-applications/${id}`)
};

// Platform Users API
export const usersAPI = {
  getUsers: (params) => api.get('/users', { params }),
  exportUsers: (params) => api.get('/users/export', { params, responseType: 'blob' }),
};

export const perksAPI = {
  getPerks: () => api.get('/perks'),
  createPerk: (data) => api.post('/perks', data),
  updatePerk: (id, data) => api.put(`/perks/${id}`, data),
  deletePerk: (id) => api.delete(`/perks/${id}`),
  getRedemptions: () => api.get('/perks/redemptions'),
  setUserPro: (userId, pro) => api.put(`/perks/user/${userId}/pro`, { pro }),
};

// Platform Updates API
export const updatesAPI = {
  getAll: (params) => api.get('/updates', { params }),
  getById: (id) => api.get(`/updates/${id}`),
  create: (data) => api.post('/updates', data),
  update: (id, data) => api.put(`/updates/${id}`, data),
  toggle: (id) => api.patch(`/updates/${id}/toggle`),
  delete: (id) => api.delete(`/updates/${id}`),
  reorder: (updates) => api.put('/updates/reorder', { updates }),
  getStats: () => api.get('/updates/stats')
};

// RAG Documents API
export const ragAPI = {
  // Documents
  getDocuments: (params) => api.get('/rag/documents', { params }),
  getDocumentById: (id) => api.get(`/rag/documents/${id}`),
  uploadDocument: (formData) => api.post('/rag/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteDocument: (id) => api.delete(`/rag/documents/${id}`),
  reindexDocument: (id) => api.post(`/rag/documents/${id}/reindex`),

  // Chunks
  getChunks: (documentId) => api.get(`/rag/documents/${documentId}/chunks`),

  // Stats & Logs
  getStats: () => api.get('/rag/stats'),
  getQueryLogs: (params) => api.get('/rag/query-logs', { params }),

  // Search (for testing)
  search: (query, params) => api.post('/rag/search', { query, ...params })
};

// Business API
export const businessAPI = {
  // Stats
  getStats: () => api.get('/business/stats'),

  // Registrations
  getAll: (params) => api.get('/business/registrations', { params }),
  getById: (id) => api.get(`/business/registrations/${id}`),
  updateStatus: (id, data) => api.patch(`/business/registrations/${id}/status`, data),
  pollStatus: (id) => api.get(`/business/registrations/${id}/poll`),

  // Providers
  getProviders: () => api.get('/business/providers'),
  createProvider: (data) => api.post('/business/providers', data),
  updateProvider: (id, data) => api.put(`/business/providers/${id}`, data),

  // Jurisdictions
  getJurisdictions: () => api.get('/business/jurisdictions'),
  createJurisdiction: (data) => api.post('/business/jurisdictions', data),
  updateJurisdiction: (id, data) => api.put(`/business/jurisdictions/${id}`, data),
  deleteJurisdiction: (id) => api.delete(`/business/jurisdictions/${id}`),

  // Activity Codes
  getActivities: (params) => api.get('/business/activities', { params }),
  createActivity: (data) => api.post('/business/activities', data),
  updateActivity: (id, data) => api.put(`/business/activities/${id}`, data),
  deleteActivity: (id) => api.delete(`/business/activities/${id}`)
};

// Invite Codes API
export const inviteCodesAPI = {
  getAll: (params) => api.get('/invite-codes', { params }),
  generate: (data) => api.post('/invite-codes/generate', data),
  delete: (id) => api.delete(`/invite-codes/${id}`),
  getStats: () => api.get('/invite-codes/stats')
};

// Conversations API (WhatsApp chat)
export const conversationsAPI = {
  get: (applicationId) => api.get(`/conversations/${applicationId}`),
  send: (applicationId, message) => api.post(`/conversations/${applicationId}/send`, { message }),
};

// Feedback API
export const feedbackAPI = {
  getAll: (params) => api.get('/feedback', { params }),
  getStats: () => api.get('/feedback/stats'),
  delete: (id) => api.delete(`/feedback/${id}`)
};

// Partners API
export const partnersAPI = {
  getAll: () => api.get('/partners'),
  getStats: () => api.get('/partners/stats'),
  create: (data) => api.post('/partners', data),
  update: (id, data) => api.patch(`/partners/${id}`, data),
  revoke: (id) => api.delete(`/partners/${id}`),
  regenerateKey: (id) => api.post(`/partners/${id}/regenerate`),
  getLedger: (id) => api.get(`/partners/${id}/ledger`),
  adjustWallet: (id, data) => api.post(`/partners/${id}/wallet-adjust`, data),
  getRequests: () => api.get('/partners/requests'),
  approveRequest: (id, data) => api.post(`/partners/requests/${id}/approve`, data),
  rejectRequest: (id) => api.post(`/partners/requests/${id}/reject`)
};

// Vendors API
export const vendorsAPI = {
  getAll: (params) => api.get('/vendors', { params }),
  getById: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  getStats: () => api.get('/vendors/stats'),
  exportConfig: (id) => api.get(`/vendors/${id}/export-config`)
};

// Discount Codes API
export const discountCodesAPI = {
  getAll: (params) => api.get('/discount-codes', { params }),
  getById: (id) => api.get(`/discount-codes/${id}`),
  create: (data) => api.post('/discount-codes', data),
  update: (id, data) => api.put(`/discount-codes/${id}`, data),
  delete: (id) => api.delete(`/discount-codes/${id}`),
  getStats: () => api.get('/discount-codes/stats')
};

export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getStats: () => api.get('/tasks/stats'),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  updateStatus: (id, status) => api.patch(`/tasks/${id}/status`, { status }),
  delete: (id) => api.delete(`/tasks/${id}`)
};

// Naytive (v3) — attorney requests + Kim voice-agent
export const naytiveAPI = {
  // User activity feed (user_context_events) — global + per-user (userId param).
  listActivity: (params) => api.get('/naytive/activity', { params }),
  listActivityTypes: () => api.get('/naytive/activity/types'),
  // Admin action audit (admin_audit_log) — the "Admin actions" tab.
  listAdminAudit: (params) => api.get('/naytive/admin-audit', { params }),
  // Create a company/entity for a client + upload docs (extraction runs in v3).
  createEntity: (payload) => api.post('/naytive/admin/create-entity', payload),
  uploadEntityDoc: (payload) => api.post('/naytive/admin/entity-doc', payload),
  listAttorneyRequests: () => api.get('/naytive/attorney-requests'),
  listIncorporationBookings: () => api.get('/naytive/incorporation-bookings'),
  getAttempts: (id) => api.get(`/naytive/attorney-requests/${id}/attempts`),
  startTestCall: (attorneyRequestId, kind = 'visa') =>
    api.post('/naytive/start-test-call', { attorneyRequestId, kind }),
  // Real phone call via Twilio — voice-agent dials the user's stored
  // phone number; no browser WebRTC. Returns { call_sid, masked_phone }.
  startRealCall: (attorneyRequestId, kind = 'visa') =>
    api.post('/naytive/start-real-call', { attorneyRequestId, kind }),
  // WebRTC offer/answer proxies. Body shape mirrors Pipecat's
  // SmallWebRTCRequest / SmallWebRTCPatchRequest.
  sendOffer: (payload) => api.post('/naytive/webrtc/offer', payload),
  sendIceCandidates: (payload) => api.patch('/naytive/webrtc/offer', payload),
  // Voice picker — list voices + their sample URLs (proxied via apbe
  // so the admin frontend doesn't need to know the voice-agent URL).
  listVoices: () => api.get('/naytive/voices'),
  voiceSampleUrl: (voice) =>
    `${process.env.REACT_APP_API_URL || ''}/naytive/voice-sample/${encodeURIComponent(voice)}`,

  // ──── Incorporation engine admin ────────────────────────────────────────
  // 15 jurisdictions + 14 scoring config keys + recommendations log all
  // live in MySQL and feed the scoring engine in user/backend. These
  // endpoints let ops edit them without a deploy.
  listIncorpJurisdictions: () => api.get('/naytive/incorporation/jurisdictions'),
  updateIncorpJurisdiction: (id, patch) =>
    api.put(`/naytive/incorporation/jurisdictions/${id}`, patch),
  listScoringConfig: () => api.get('/naytive/incorporation/scoring-config'),
  updateScoringConfig: (key, value) =>
    api.put(`/naytive/incorporation/scoring-config/${encodeURIComponent(key)}`, { value }),
  listRecommendations: (filters = {}) =>
    api.get('/naytive/incorporation/recommendations', { params: filters }),
  reloadIncorpEngine: () => api.post('/naytive/incorporation/reload-engine'),

  // ──── App feature flags (Naytive Apps → App Flags) ───────────────────────
  // Admin-controlled UI toggles the v3 SPA reads live via /api/naytive/flags.
  listFeatureFlags: () => api.get('/naytive/feature-flags'),
  updateFeatureFlag: (key, enabled) =>
    api.put(`/naytive/feature-flags/${encodeURIComponent(key)}`, { enabled }),

  // ──── Live chat (agents reply to founder support threads) ────────────────
  listChats: () => api.get('/naytive/chats'),
  getChatMessages: (id, after = 0) =>
    api.get(`/naytive/chats/${id}/messages`, { params: after ? { after } : {} }),
  sendChatReply: (id, body) => api.post(`/naytive/chats/${id}/messages`, { body }),
  requestChatDoc: (id, label, note) => api.post(`/naytive/chats/${id}/request-doc`, { label, note }),
  uploadChatFile: (id, payload) => api.post(`/naytive/chats/${id}/upload`, payload),
  getChatFileUrl: (id, key) => api.get(`/naytive/chats/${id}/file`, { params: { key } }),
  searchChatUsers: (q) => api.get('/naytive/chat-users', { params: q ? { q } : {} }),
  startChat: (userId) => api.post('/naytive/chats/start', { userId }),

  // ──── Compliance call requests ──────────────────────────────────────────
  // Compliance "Schedule a call" submissions from the v3 calendar drawer.
  // These DO NOT go through the voice-agent scheduler — admins work
  // through the queue manually (status update + appended notes).
  listComplianceCallRequests: (params = {}) =>
    api.get('/naytive/compliance-call-requests', { params }),
  updateComplianceCallRequest: (id, patch) =>
    api.patch(`/naytive/compliance-call-requests/${id}`, patch),

  // ──── USD bank-account waitlist (user panel "Join waitlist" flow) ────────
  listBankingWaitlist: (params = {}) =>
    api.get('/naytive/banking-waitlist', { params }),
  updateBankingWaitlist: (id, patch) =>
    api.patch(`/naytive/banking-waitlist/${id}`, patch),

  // ──── Banking access requests (app "Request access", banking invite-only) ──
  listBankingAccessRequests: (params = {}) =>
    api.get('/naytive/banking-access-requests', { params }),
  getBankingAccessRequest: (id) =>
    api.get(`/naytive/banking-access-requests/${id}`),
  updateBankingAccessRequest: (id, patch) =>
    api.patch(`/naytive/banking-access-requests/${id}`, patch),

  // ──── Landing-page invitation leads (naytive.com "Request an invitation") ──
  listLandingLeads: () => api.get('/naytive/landing-leads'),
  deleteLandingLead: (id) => api.delete(`/naytive/landing-leads/${id}`),

  // ──── Blog CMS (mounted at /api/blog, not /api/naytive) ──────────────────
  listBlogPosts: (params = {}) => api.get('/blog/posts', { params }),
  getBlogPost: (id) => api.get(`/blog/posts/${id}`),
  createBlogPost: (body) => api.post('/blog/posts', body),
  updateBlogPost: (id, body) => api.patch(`/blog/posts/${id}`, body),
  deleteBlogPost: (id) => api.delete(`/blog/posts/${id}`),
  listBlogCategories: () => api.get('/blog/categories'),
  createBlogCategory: (body) => api.post('/blog/categories', body),
  updateBlogCategory: (id, body) => api.patch(`/blog/categories/${id}`, body),
  deleteBlogCategory: (id) => api.delete(`/blog/categories/${id}`),
  blogAiGenerate: (body) => api.post('/blog/ai/generate', body),
  blogAiSeo: (body) => api.post('/blog/ai/seo', body),
  uploadBlogImage: (body) => api.post('/blog/upload-image', body),

  // ──── Careers — job postings + applications ──────────────────────────────
  listJobs: () => api.get('/careers/jobs'),
  getJob: (id) => api.get(`/careers/jobs/${id}`),
  createJob: (body) => api.post('/careers/jobs', body),
  updateJob: (id, body) => api.patch(`/careers/jobs/${id}`, body),
  deleteJob: (id) => api.delete(`/careers/jobs/${id}`),
  listApplications: (params = {}) => api.get('/careers/applications', { params }),
  getApplication: (id) => api.get(`/careers/applications/${id}`),
  updateApplication: (id, body) => api.patch(`/careers/applications/${id}`, body),
  getApplicationResume: (id, dl = false) =>
    api.get(`/careers/applications/${id}/resume${dl ? '?dl=1' : ''}`, { responseType: 'blob' }),

  // Warm leads captured on "Get help with this filing" voice calls — the
  // compliance agent flagged the founder would benefit from a specialist
  // handling the filing. Carries the call transcript for qualification.
  listComplianceLeads: (params = {}) =>
    api.get('/naytive/compliance-leads', { params }),
  updateComplianceLead: (id, patch) =>
    api.patch(`/naytive/compliance-leads/${id}`, patch),

  // ──── Jurisdiction pricing components ───────────────────────────────────
  // Tentative pricing rows per jurisdiction. user-panel-v3's
  // autoCreateProposalForBooking copies these into a new proposal's
  // pricing_json when a booking lands.
  listPricingComponents: (params = {}) =>
    api.get('/naytive/jurisdiction-pricing-components', { params }),
  createPricingComponent: (payload) =>
    api.post('/naytive/jurisdiction-pricing-components', payload),
  updatePricingComponent: (id, patch) =>
    api.patch(`/naytive/jurisdiction-pricing-components/${id}`, patch),
  deletePricingComponent: (id) =>
    api.delete(`/naytive/jurisdiction-pricing-components/${id}`),

  // ──── Incorporation proposals (queue) ───────────────────────────────────
  // One proposal per case. Auto-created as `tentative` on booking;
  // admin edits per-row pricing and flips pricing_kind to `final` to
  // publish a case-specific quote that re-enables the Accept CTA on
  // the user dashboard drawer.
  listIncorporationProposals: (params = {}) =>
    api.get('/naytive/incorporation-proposals', { params }),
  getIncorporationProposal: (id) =>
    api.get(`/naytive/incorporation-proposals/${id}`),
  updateIncorporationProposal: (id, patch) =>
    api.patch(`/naytive/incorporation-proposals/${id}`, patch),
  // "Mark incorporation complete" — materialises a real `entities` row for the
  // founder and links it back onto the proposal. Body: { name, legal_type,
  // incorporation_date, ein?, state_or_region?, foreign_owned?, has_employees?,
  // parent_entity_name?, notes? }.
  completeIncorporation: (id, body) =>
    api.post(`/naytive/incorporation-proposals/${id}/complete`, body),
  // Team-uploaded entity documents (certificate of incorporation + any others).
  // Written to the founder's vault so they show on their Company page.
  listProposalDocuments: (id) =>
    api.get(`/naytive/incorporation-proposals/${id}/documents`),
  uploadProposalDocument: (id, body) =>
    api.post(`/naytive/incorporation-proposals/${id}/documents`, body),
  downloadUserDocument: (docId) =>
    api.get(`/naytive/user-documents/${docId}/file`, { responseType: 'blob' }),
  downloadProposalPacket: (id) =>
    api.get(`/naytive/incorporation-proposals/${id}/packet`, { responseType: 'blob' }),

  // ──── Email — templates + send to a founder ────────────────────────────
  listEmailTemplates: () => api.get('/naytive/email-templates'),
  createEmailTemplate: (body) => api.post('/naytive/email-templates', body),
  updateEmailTemplate: (id, body) => api.patch(`/naytive/email-templates/${id}`, body),
  deleteEmailTemplate: (id) => api.delete(`/naytive/email-templates/${id}`),
  sendEmail: (body) => api.post('/naytive/send-email', body),

  // ──── Email inbox — received mail synced from IMAP + sent from email_log ─
  syncEmails: () => api.post('/naytive/emails/sync'),
  listEmails: (params = {}) => api.get('/naytive/emails', { params }),
  getInboundEmail: (id) => api.get(`/naytive/emails/in/${id}`),
  getSentEmail: (id) => api.get(`/naytive/emails/out/${id}`),
  markEmailRead: (id, is_read) => api.patch(`/naytive/emails/in/${id}`, { is_read }),

  // ──── Compliance cockpit — per-entity filing calendar ───────────────────
  // Reads the compliance_filings the user-panel engine materialised; admin does
  // simple state writes (mark filed / add ad-hoc / re-run generation).
  listFilingTemplates: (params = {}) =>
    api.get('/naytive/filing-templates', { params }),
  // Filing-template editor (Phase 5) — the editable "what compliance comes next".
  getFilingTemplate: (id) => api.get(`/naytive/filing-templates/${id}`),
  createFilingTemplate: (body) => api.post('/naytive/filing-templates', body),
  updateFilingTemplate: (id, patch) => api.patch(`/naytive/filing-templates/${id}`, patch),
  addFilingTemplateRule: (id, body) => api.post(`/naytive/filing-templates/${id}/rules`, body),
  deleteFilingTemplateRule: (id, ruleId) =>
    api.delete(`/naytive/filing-templates/${id}/rules/${ruleId}`),
  listEntityFilings: (entityId) =>
    api.get(`/naytive/entities/${entityId}/filings`),
  addEntityFiling: (entityId, body) =>
    api.post(`/naytive/entities/${entityId}/filings`, body),
  regenerateEntityFilings: (entityId) =>
    api.post(`/naytive/entities/${entityId}/regenerate-filings`),
  updateComplianceFiling: (id, patch) =>
    api.patch(`/naytive/compliance-filings/${id}`, patch),

  // ──── Per-company vault documents ─────────────────────────────────────
  listEntityDocuments: (entityId) =>
    api.get(`/naytive/entities/${entityId}/documents`),
  uploadEntityDocument: (entityId, body) =>
    api.post(`/naytive/entities/${entityId}/documents`, body),

  // ──── Offline deals + installment billing ─────────────────────────────
  listDeals: () => api.get('/naytive/deals'),
  getDeal: (id) => api.get(`/naytive/deals/${id}`),
  createDeal: (body) => api.post('/naytive/deals', body),
  sendMilestoneLink: (milestoneId) => api.post(`/naytive/deal-milestones/${milestoneId}/send-link`),
  updateMilestone: (milestoneId, patch) => api.patch(`/naytive/deal-milestones/${milestoneId}`, patch),

  // ──── Jurisdiction required documents ─────────────────────────────────
  // Per-jurisdiction default doc list — copied into proposal.requirements_json
  // by auto-create-proposal so users see what we need before any admin work.
  listRequiredDocs: (params = {}) =>
    api.get('/naytive/jurisdiction-required-documents', { params }),
  createRequiredDoc: (body) =>
    api.post('/naytive/jurisdiction-required-documents', body),
  updateRequiredDoc: (id, patch) =>
    api.patch(`/naytive/jurisdiction-required-documents/${id}`, patch),
  deleteRequiredDoc: (id) =>
    api.delete(`/naytive/jurisdiction-required-documents/${id}`),
  // Global active document types catalog — picker for the modal.
  listDocumentTypes: () => api.get('/naytive/document-types'),

  // ──── Proposal stage events (Option C — per-stage audit trail) ──────────
  // Append-only events per proposal stage. Drives both the user's
  // "where am I in my setup" UI and the admin per-case timeline log.
  listStageEvents: (proposalId) =>
    api.get(`/naytive/incorporation-proposals/${proposalId}/stage-events`),
  addStageEvent: (proposalId, body) =>
    api.post(`/naytive/incorporation-proposals/${proposalId}/stage-events`, body),
  deleteStageEvent: (proposalId, eventId) =>
    api.delete(`/naytive/incorporation-proposals/${proposalId}/stage-events/${eventId}`),

  // ──── Proposal actions (powers user-side Actions counter) ──────────────
  // Mutable to-do items per proposal. Drives the "Actions needed N / M"
  // counter on the user's setup-overview card.
  listProposalActions: (proposalId) =>
    api.get(`/naytive/incorporation-proposals/${proposalId}/actions`),
  addProposalAction: (proposalId, body) =>
    api.post(`/naytive/incorporation-proposals/${proposalId}/actions`, body),
  updateProposalAction: (proposalId, actionId, patch) =>
    api.patch(`/naytive/incorporation-proposals/${proposalId}/actions/${actionId}`, patch),
  deleteProposalAction: (proposalId, actionId) =>
    api.delete(`/naytive/incorporation-proposals/${proposalId}/actions/${actionId}`),
};

// Calls API — unified admin view over attorney_requests (visa) +
// incorporation_bookings (incorporation), each with their own notes
// (call_notes) and per-call requested-doc list (call_required_docs).
export const callsAPI = {
  list: (params = {}) => api.get('/calls', { params }),
  get: (kind, id) => api.get(`/calls/${kind}/${id}`),
  updateStatus: (kind, id, status) => api.patch(`/calls/${kind}/${id}`, { status }),
  addNote: (kind, id, body) => api.post(`/calls/${kind}/${id}/notes`, { body }),
  deleteNote: (kind, id, noteId) =>
    api.delete(`/calls/${kind}/${id}/notes/${noteId}`),
  requestDoc: (kind, id, documentTypeId, notes) =>
    api.post(`/calls/${kind}/${id}/requested-docs`, { documentTypeId, notes }),
  revokeDocRequest: (kind, id, rdid) =>
    api.delete(`/calls/${kind}/${id}/requested-docs/${rdid}`),
};

// Document types (catalog for the "request doc" picker in Calls).
export const documentTypesAPI = {
  list: () => api.get('/visa/document-types'),
};

// Persona Skills — the advisor's domain knowledge (L2), editable here so
// the team can author rubrics/playbooks without code. Backs the AI
// personas (immigration / incorporation / compliance / banking / concierge).
export const personaSkillsAPI = {
  getAll: (params = {}) => api.get('/persona-skills', { params }),
  create: (data) => api.post('/persona-skills', data),
  update: (id, data) => api.put(`/persona-skills/${id}`, data),
  delete: (id) => api.delete(`/persona-skills/${id}`),
};

// Visa criteria / rubrics / weights / evidence + per-visa eligibility rules —
// the admin-tunable substrate behind the visa scoring engine.
export const visaCriteriaAPI = {
  getVisas: () => api.get('/visa-criteria/visas'),
  getCriteria: (visaConfigId) => api.get('/visa-criteria', { params: { visaConfigId } }),
  create: (data) => api.post('/visa-criteria', data),
  update: (id, data) => api.put(`/visa-criteria/${id}`, data),
  delete: (id) => api.delete(`/visa-criteria/${id}`),
  updateEligibility: (visaConfigId, eligibilityRules) =>
    api.put(`/visa-criteria/eligibility/${visaConfigId}`, { eligibilityRules }),
  updatePricing: (visaConfigId, pricing) =>
    api.put(`/visa-criteria/pricing/${visaConfigId}`, pricing),
};

// Compliance Pro — jurisdiction-wise upsell config (offering + coverage +
// add-ons) shown at the coverage step of the v3 company-import flow.
export const complianceProAPI = {
  list: () => api.get('/compliance-pro'),
  get: (code) => api.get(`/compliance-pro/${code}`),
  save: (code, data) => api.put(`/compliance-pro/${code}`, data),
  delete: (code) => api.delete(`/compliance-pro/${code}`),
};

export default api;
